import React, { useState, useEffect, useMemo } from "react";
import { Link } from 'react-router-dom';
import Loader from '../shared/Loader';
import { getDisplayImageUrl } from '../../utils/imageUtils';
import 'bootstrap/dist/css/bootstrap.min.css';
import '@fortawesome/fontawesome-free/css/all.min.css';

// Iconos para cada categoría
const categoryIcons = {
  'remeras': 'fa-solid fa-tshirt',
  'buzos': 'fa-solid fa-hoodie',
  'canguros': 'fa-solid fa-hoodie',
  'cuadros': 'fa-solid fa-image',
  'almohadones': 'fa-solid fa-couch',
  'merchandising': 'fa-solid fa-pen-ruler',
  'accesorios': 'fa-solid fa-gem',
  'hogar': 'fa-solid fa-home',
  'ropa': 'fa-solid fa-tshirt',
  'ninos': 'fa-solid fa-child',
  'default': 'fa-solid fa-tag'
};

// Colores verdes para las categorías como fallback
const greenColors = [
  '#1f8e38', '#25a000', '#2d5a0b', '#4a7c59',
  '#388E3C', '#43A047', '#4CAF50', '#66BB6A',
  '#81C784', '#A5D6A7', '#C8E6C9'
];

// Imágenes por defecto para cada categoría
const categoryImages = {
  'remeras': '/images/categories/remeras.png',
  'buzos': '/images/categories/buzos.png',
  'canguros': '/images/categories/canguros.png',
  'cuadros': '/images/categories/cuadros.png',
  'almohadones': '/images/categories/almohadones.png',
  'merchandising': '/images/categories/merchandising.png',
  'accesorios': '/images/categories/accesorios.png',
  'hogar': '/images/categories/hogar.png',
  'ropa': '/images/categories/ropa.png',
  'tienda': '/images/categories/tienda.png',
  'ninos': '/images/categories/default.png',
  'default': '/images/categories/default.png'
};

const normalizeCategoryKey = (value = '') =>
  value
    .toString()
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9-_]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '') || 'default';

const buildCategoryTiles = (categories = []) => {
  const tiles = [
    {
      _id: 'tienda',
      name: 'Tienda',
      displayName: 'Tienda',
      description: 'Ver todos los productos',
      imageKey: 'tienda',
      href: '/tienda'
    }
  ];

  categories.forEach((category) => {
    const activeSubs = (category.subcategories || []).filter(sub => (sub.productCount || 0) > 0);
    const expandSubs = category.name === 'Niños' && activeSubs.length > 0;

    // Niños se muestra por subcategoría cuando hay productos (Canguros / Remeras)
    if (expandSubs) {
      activeSubs.forEach((sub) => {
        tiles.push({
          _id: `${category._id}-${sub._id || sub.slug || sub.name}`,
          name: `${category.name} · ${sub.name}`,
          displayName: `${category.name} · ${sub.name}`,
          description: sub.description || category.description || '',
          imageKey: normalizeCategoryKey(`${category.name}-${sub.name}`),
          fallbackImageKey: normalizeCategoryKey(category.name),
          href: `/tienda?category=${encodeURIComponent(category.name)}&subcategory=${encodeURIComponent(sub.name)}`,
          iconName: sub.name
        });
      });
      return;
    }

    tiles.push({
      _id: category._id,
      name: category.name,
      displayName: category.name,
      description: category.description || '',
      imageKey: normalizeCategoryKey(category.name),
      fallbackImageKey: normalizeCategoryKey(category.name),
      href: `/tienda?category=${encodeURIComponent(category.name)}`,
      iconName: category.name
    });
  });

  return tiles;
};

const OfferGrid = () => {
  const [tiles, setTiles] = useState([]);
  const [homeImages, setHomeImages] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const [categoriesResponse, settingsResponse] = await Promise.all([
        fetch('/api/categories'),
        fetch('/api/settings')
      ]);
      const data = await categoriesResponse.json();
      const settingsData = settingsResponse.ok ? await settingsResponse.json() : {};

      setTiles(buildCategoryTiles(Array.isArray(data) ? data : []));
      setHomeImages(settingsData?.homeImages?.categories || {});
    } catch (error) {
      console.error('Error fetching categories:', error);
      setTiles([
        { _id: 'tienda', name: 'Tienda', displayName: 'Tienda', imageKey: 'tienda', href: '/tienda' },
        { _id: '1', name: 'Remeras', displayName: 'Remeras', imageKey: 'remeras', href: '/tienda?category=Remeras' },
        { _id: '2', name: 'Buzos', displayName: 'Buzos', imageKey: 'buzos', href: '/tienda?category=Buzos' },
        { _id: '3', name: 'Cuadros', displayName: 'Cuadros', imageKey: 'cuadros', href: '/tienda?category=Cuadros' }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const visibleTiles = useMemo(() => tiles.slice(0, 8), [tiles]);

  const getCategoryIcon = (categoryName) => {
    const name = normalizeCategoryKey(categoryName || '');
    return categoryIcons[name] || categoryIcons.default;
  };

  const getCategoryColor = (index) => {
    return greenColors[index % greenColors.length];
  };

  const getCategorySize = (index) => {
    if (index < 4) return "col-6 col-md-3";
    return "col-6 col-md-6";
  };

  const getCategoryBackgroundImage = (tile, index) => {
    const primaryKey = tile.imageKey || normalizeCategoryKey(tile.name);
    const fallbackKey = tile.fallbackImageKey || primaryKey;
    const bgImage = getDisplayImageUrl(
      homeImages[primaryKey] ||
      homeImages[fallbackKey] ||
      categoryImages[primaryKey] ||
      categoryImages[fallbackKey] ||
      categoryImages.default
    );
    const fallbackColor = getCategoryColor(index);

    return `linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.2) 50%, rgba(0,0,0,0.05) 100%), 
            url('${bgImage}'), 
            linear-gradient(${fallbackColor}, ${fallbackColor})`;
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <section className="categories-grid py-5 bg-white">
      <div className="container">
        <div className="text-center mb-5 section-header">
          <h2 className="fw-bold modern-title">Explora Nuestras Categorías</h2>
          <p className="text-muted fs-5">Encontrá el producto perfecto para tu estilo.</p>
        </div>
        <div className="row h-100 g-3 g-md-4">
          {visibleTiles.map((tile, index) => (
            <div key={tile._id} className={`${getCategorySize(index)} d-flex align-items-stretch`}>
              <Link
                to={tile.href}
                className="d-flex flex-column align-items-center justify-content-end w-100 text-white text-decoration-none category-card rounded-4 p-4 shadow-sm"
                style={{
                  background: getCategoryBackgroundImage(tile, index),
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundBlendMode: 'normal, normal, normal',
                  height: '300px',
                  width: '100%',
                  transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                <div className="category-overlay-anim"></div>
                <div className="category-content-container d-flex flex-column align-items-center position-relative z-1 mb-2">
                  <div className="category-icon-bg mb-3">
                    <i className={`${getCategoryIcon(tile.iconName || tile.name)} fa-2x category-icon-modern`}></i>
                  </div>
                  <h3 className="h4 fw-bold text-center category-title-modern mb-0">{tile.displayName || tile.name}</h3>
                </div>
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default OfferGrid;
